import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

// Voice and SMS share the request/stream lifecycle; the server owns expansion.
// Neither previews nor counters replace the shared form or textarea.
export class MessagePreviewController extends Controller {
    static targets = ["input", "notice", "preview", "count"]
    static values = {
        url: String,
        field: { type: String, default: "call-message.body" },
        output: { type: String, default: "call-message-preview" },
        sms: { type: Boolean, default: false },
        limit: { type: Number, default: 1000 }
    }

    connect() {
        this.stopped = false
        this.sequence = (this.sequence ?? 0) + 1
        this.beforeCache = () => this.disconnect()
        document.addEventListener("turbo:before-cache", this.beforeCache)
        // Also handles restored drafts, without ever alerting/sending a message.
        if (this.smsValue) this.refresh()
    }

    disconnect() {
        this.stopped = true
        this.invalidate()
        document.removeEventListener("turbo:before-cache", this.beforeCache)
    }

    invalidate() {
        this.sequence += 1
        clearTimeout(this.timer)
        this.abortController?.abort()
    }

    insertVariable(event) {
        event.preventDefault()
        const variable = event.params.variable
        const input = this.inputTarget
        if (this.stopped || input.disabled || input.readOnly || typeof variable !== "string" || !variable) return
        // The textarea retains its selection while the button has focus. Capture
        // it before refocusing, for both pointer and keyboard activation.
        const start = input.selectionStart
        const end = input.selectionEnd
        if (input.maxLength >= 0 && input.value.length - (end - start) + variable.length > input.maxLength) {
            this.say("The message template is too long to insert this variable.")
            return
        }
        input.focus({ preventScroll: true })
        input.setRangeText(variable, start, end, "end")
        // setRangeText does not emit input. Notify the shared resize/validation
        // controllers and the normal preview/count lifecycle without submitting.
        input.dispatchEvent(new Event("input", { bubbles: true }))
    }

    schedule() {
        if (this.stopped) return
        this.invalidate()
        if (!this.prepare()) return
        this.timer = setTimeout(() => this.refresh(), 300)
    }

    prepare() {
        if (this.smsValue) {
            const draft = this.inputTarget.value
            const dynamic = /\{store_hours/i.test(draft)
            this.previewTarget.hidden = !dynamic
            if (!dynamic) {
                this.previewTarget.textContent = ""
                this.countTarget.textContent = `${draft.length} / ${this.limitValue}`
                this.countTarget.classList.toggle("text-error", draft.length > this.limitValue)
                this.say("")
                return false
            }
            // Never label the raw template length as the message's count.
            this.countTarget.textContent = `… / ${this.limitValue}`
            this.countTarget.classList.remove("text-error")
            this.previewTarget.textContent = "Updating preview…"
        }
        this.say("Updating preview…")
        return true
    }

    unavailable(text) {
        if (this.smsValue) {
            this.countTarget.textContent = `— / ${this.limitValue}`
            this.previewTarget.textContent = "Preview unavailable."
        }
        this.say(text)
    }

    async refresh() {
        if (this.stopped) return
        this.invalidate()
        if (!this.prepare()) return
        const sequence = this.sequence
        const draft = this.inputTarget.value
        // yesod-ui associates controls with an otherwise empty form using the
        // HTML form attribute. closest("form") would not find it.
        const csrf = this.inputTarget.form?.elements.namedItem("_token")?.value
        if (!csrf) {
            this.unavailable("Preview unavailable. Reload the page to refresh your session; your draft is unchanged.")
            return
        }
        const body = new URLSearchParams({ [this.fieldValue]: draft, _token: csrf })
        const abortController = new AbortController()
        this.abortController = abortController
        const deadline = setTimeout(() => abortController.abort(), 5000)
        this.say("Updating preview…")
        try {
            const response = await fetch(this.urlValue, {
                method: "POST", credentials: "same-origin", cache: "no-store",
                signal: abortController.signal,
                headers: { Accept: "text/vnd.turbo-stream.html", "X-XSRF-TOKEN": csrf }, body
            })
            if (!this.current(sequence, draft)) return
            if (response.redirected || response.status === 401 || response.status === 403) {
                this.unavailable("Sign in again to preview this message. Your draft is unchanged.")
                return
            }
            if (![200, 422, 503].includes(response.status) ||
                !response.headers.get("Content-Type")?.startsWith("text/vnd.turbo-stream.html")) {
                throw new Error("Preview unavailable")
            }
            const html = await response.text()
            if (!this.current(sequence, draft)) return
            // Turbo schedules rendering. Guard the actual render too, so typing
            // between receipt and the next animation frame cannot apply stale HTML.
            const beforeRender = event => {
                const target = event.target.getAttribute("target")
                if (target !== this.outputValue && !(this.smsValue && target === `${this.outputValue}-count`)) return
                const render = event.detail.render
                event.detail.render = element => {
                    if (this.current(sequence, draft)) return render(element)
                }
            }
            document.addEventListener("turbo:before-stream-render", beforeRender)
            try { Turbo.renderStreamMessage(html) }
            finally { document.removeEventListener("turbo:before-stream-render", beforeRender) }
            this.say("")
        } catch (error) {
            if (this.current(sequence, draft)) {
                this.unavailable("Unable to update the preview. Your draft is still here; change the message to retry.")
            }
        } finally {
            clearTimeout(deadline)
        }
    }

    current(sequence, draft) {
        return !this.stopped && sequence === this.sequence && this.inputTarget.value === draft
    }

    say(text) { this.noticeTarget.textContent = text }
}
